import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Loader2, ClipboardCheck, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending_brand_review:{ label: 'Awaiting your review', color: '#FFB800' },
  finalized:           { label: 'Finalized',            color: '#0055FF' },
  outreach_started:    { label: 'Outreach Started',     color: '#00C972' },
};

// Brand inbox of review lists submitted by Campaign Managers.
export const BrandReviewLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/review-lists`, { withCredentials: true });
        setLists(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, []);

  const pending = lists.filter((l) => l.status === 'pending_brand_review');
  const completed = lists.filter((l) => l.status !== 'pending_brand_review');

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-review-lists-page">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
            <ClipboardCheck className="text-[#FF451A]" size={32} />
            Creator Review Lists
          </h1>
          <p className="text-[#6B6B6B] dark:text-zinc-400 mb-8">Pick which creators to bring into your campaigns. Shortlist or reject each one, then finalize.</p>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : lists.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="brand-review-empty">
              <ClipboardCheck className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No review lists yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">Your Campaign Manager will send creators for selection soon.</p>
            </div>
          ) : (
            <>
              {pending.length > 0 && (
                <section className="mb-8" data-testid="brand-review-pending">
                  <div className="text-[10px] uppercase tracking-wider text-[#FFB800] mb-3 font-mono">Awaiting your review · {pending.length}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{pending.map((rl) => <Card key={rl.id} rl={rl} />)}</div>
                </section>
              )}
              {completed.length > 0 && (
                <section data-testid="brand-review-history">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-3 font-mono">History · {completed.length}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{completed.map((rl) => <Card key={rl.id} rl={rl} />)}</div>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Card = ({ rl }) => {
  const meta = STATUS_META[rl.status] || STATUS_META.pending_brand_review;
  return (
    <Link to={`/brand/review-lists/${rl.id}`} className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:-translate-y-[2px] transition-all block" data-testid="brand-review-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate">{rl.name}</div>
          <div className="text-xs text-[#6B6B6B] dark:text-zinc-500">{rl.campaign_name}</div>
        </div>
        <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full shrink-0"
          style={{ backgroundColor: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}>
          {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        {[
          { l: 'Total',       v: rl.summary?.total ?? 0,       c: '#71717a' },
          { l: 'Shortlisted', v: rl.summary?.shortlisted ?? 0, c: '#00C972' },
          { l: 'Rejected',    v: rl.summary?.rejected ?? 0,    c: '#FF3B30' },
          { l: 'Remaining',   v: rl.summary?.remaining ?? 0,   c: '#FFB800' },
        ].map((t) => (
          <div key={t.l} className="bg-white/5 border border-black/[0.10] dark:border-white/10 rounded-lg p-2">
            <div className="text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">{t.l}</div>
            <div className="text-base font-bold font-mono" style={{ color: t.c }}>{t.v}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-[#FF451A] inline-flex items-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
        Open <ArrowUpRight size={11} />
      </div>
    </Link>
  );
};
